import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  DragIndicator as DragIcon,
  SwapVert as SwapIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  TableChart as TableIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const ColumnMergeDialog = ({ 
  open, 
  onClose, 
  existingColumns = [], 
  newColumns = [], 
  tableName = "",
  onConfirmMerge,
  loading = false 
}) => {
  // State for column mappings and merge strategy
  const [columnMappings, setColumnMappings] = useState({});
  const [mergeStrategy, setMergeStrategy] = useState({
    addNewColumns: true,
    updateExistingData: false,
    appendUniqueRecords: true
  });
  const [mappedNewColumns, setMappedNewColumns] = useState([]);
  const [unmappedColumns, setUnmappedColumns] = useState([]);

  // Normalize incoming columns so we can handle strings or objects uniformly
  const normalizedExistingColumns = useMemo(() => {
    return (existingColumns || [])
      .map((col) => {
        if (typeof col === 'string') {
          return { name: col, type: 'TEXT' };
        }
        if (col && typeof col === 'object') {
          return { name: col.name ?? '', type: col.type || 'TEXT', ...col };
        }
        return { name: '', type: 'TEXT' };
      })
      .filter((c) => c.name !== '');
  }, [existingColumns]);

  const normalizedNewColumns = useMemo(() => {
    return (newColumns || [])
      .map((col) => {
        if (typeof col === 'string') {
          return { name: col, type: 'TEXT' };
        }
        if (col && typeof col === 'object') {
          return { name: col.name ?? '', type: col.type || 'TEXT', ...col };
        }
        return { name: '', type: 'TEXT' };
      })
      .filter((c) => c.name !== '');
  }, [newColumns]);

  // Initialize column analysis when dialog opens
  useEffect(() => {
    if (open && normalizedExistingColumns.length > 0 && normalizedNewColumns.length > 0) {
      analyzeColumnMatches();
    }
  }, [open, normalizedExistingColumns, normalizedNewColumns]);

  const analyzeColumnMatches = () => {
    const mappings = {};
    const mapped = [];
    const unmapped = [];

    // Create normalized versions for better matching
    const normalizeColumnName = (name) => {
      if (typeof name !== 'string') return '';
      return name.toLowerCase().replace(/[^a-z0-9]/g, '');
    };

    // First pass: exact matches
    normalizedNewColumns.forEach((newCol, newIndex) => {
      const exactMatch = normalizedExistingColumns.find(existingCol => 
        normalizeColumnName(newCol.name) === normalizeColumnName(existingCol.name)
      );
      
      if (exactMatch) {
        const existingIndex = normalizedExistingColumns.findIndex(col => col.name === exactMatch.name);
        mappings[newIndex] = existingIndex;
        mapped.push({
          ...newCol,
          originalIndex: newIndex,
          mappedToIndex: existingIndex,
          mappedToColumn: exactMatch,
          matchType: 'exact'
        });
      } else {
        // Check for fuzzy matches (similar names)
        const fuzzyMatch = normalizedExistingColumns.find(existingCol => {
          const similarity = calculateSimilarity(
            normalizeColumnName(newCol.name), 
            normalizeColumnName(existingCol.name)
          );
          return similarity > 0.7; // 70% similarity threshold
        });

        if (fuzzyMatch) {
          const existingIndex = normalizedExistingColumns.findIndex(col => col.name === fuzzyMatch.name);
          mapped.push({
            ...newCol,
            originalIndex: newIndex,
            mappedToIndex: existingIndex,
            mappedToColumn: fuzzyMatch,
            matchType: 'fuzzy',
            needsConfirmation: true
          });
        } else {
          unmapped.push({
            ...newCol,
            originalIndex: newIndex,
            matchType: 'new'
          });
        }
      }
    });

    setColumnMappings(mappings);
    setMappedNewColumns(mapped);
    setUnmappedColumns(unmapped);
  };

  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    if (source.droppableId === "newColumns" && destination.droppableId === "existingColumns") {
      // Map new column to existing column
      const newColumnIndex = parseInt(source.index);
      const existingColumnIndex = parseInt(destination.index);
      
      setColumnMappings(prev => ({
        ...prev,
        [newColumnIndex]: existingColumnIndex
      }));

      // Update mapped columns
      const draggedColumn = normalizedNewColumns[newColumnIndex];
      const targetColumn = normalizedExistingColumns[existingColumnIndex];
      
      setMappedNewColumns(prev => {
        const updated = prev.filter(col => col.originalIndex !== newColumnIndex);
        updated.push({
          ...draggedColumn,
          originalIndex: newColumnIndex,
          mappedToIndex: existingColumnIndex,
          mappedToColumn: targetColumn,
          matchType: 'manual'
        });
        return updated;
      });

      // Remove from unmapped if it was there
      setUnmappedColumns(prev => 
        prev.filter(col => col.originalIndex !== newColumnIndex)
      );
    }
  };

  const handleRemoveMapping = (newColumnIndex) => {
    setColumnMappings(prev => {
      const updated = { ...prev };
      delete updated[newColumnIndex];
      return updated;
    });

    // Move back to unmapped
    const column = normalizedNewColumns[newColumnIndex];
    setUnmappedColumns(prev => [...prev, {
      ...column,
      originalIndex: newColumnIndex,
      matchType: 'new'
    }]);

    // Remove from mapped
    setMappedNewColumns(prev => 
      prev.filter(col => col.originalIndex !== newColumnIndex)
    );
  };

  const handleConfirmMerge = () => {
    const mergeConfig = {
      columnMappings,
      mappedColumns: mappedNewColumns,
      newColumns: unmappedColumns,
      mergeStrategy,
      existingTableColumns: normalizedExistingColumns,
      newFileColumns: normalizedNewColumns
    };

    onConfirmMerge(mergeConfig);
  };

  const getColumnMatchColor = (matchType) => {
    switch (matchType) {
      case 'exact': return 'success';
      case 'fuzzy': return 'warning';
      case 'manual': return 'info';
      case 'new': return 'default';
      default: return 'default';
    }
  };

  const getMatchIcon = (matchType) => {
    switch (matchType) {
      case 'exact': return <CheckIcon />;
      case 'fuzzy': return <WarningIcon />;
      case 'manual': return <SwapIcon />;
      case 'new': return <AddIcon />;
      default: return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '900px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableIcon />
          <Typography variant="h6">
            Smart Merge: {tableName}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Compare and map columns between existing table and new file
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Analyzing column structure...
            </Typography>
          </Box>
        )}

        {/* Summary Alert */}
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<TableIcon />}
        >
          <Typography variant="subtitle2" gutterBottom>
            Column Analysis Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              size="small" 
              color="success" 
              label={`${mappedNewColumns.filter(c => c.matchType === 'exact').length} Exact Matches`}
            />
            <Chip 
              size="small" 
              color="warning" 
              label={`${mappedNewColumns.filter(c => c.matchType === 'fuzzy').length} Similar Matches`}
            />
            <Chip 
              size="small" 
              color="default" 
              label={`${unmappedColumns.length} New Columns`}
            />
          </Box>
        </Alert>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Grid container spacing={3}>
            {/* Existing Table Columns */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableIcon color="primary" />
                    Existing Table: {tableName}
                    <Chip size="small" label={`${normalizedExistingColumns.length} columns`} />
                  </Typography>
                  
                  <Droppable droppableId="existingColumns" type="COLUMN">
                    {(provided, snapshot) => (
                      <List
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{ 
                          minHeight: 300,
                          maxHeight: 400,
                          overflow: 'auto',
                          bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                          border: snapshot.isDraggingOver ? '2px dashed' : '1px solid',
                          borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          p: 1
                        }}
                      >
                        {normalizedExistingColumns.map((column, index) => {
                          const isMapped = Object.values(columnMappings).includes(index);
                          const mappedColumn = mappedNewColumns.find(c => c.mappedToIndex === index);
                          
                          return (
                            <Draggable 
                              key={`existing-${index}`} 
                              draggableId={`existing-${index}`} 
                              index={index}
                              isDragDisabled={true}
                            >
                              {(provided, snapshot) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    mb: 1,
                                    bgcolor: isMapped ? 'success.light' : 'background.paper',
                                    border: '1px solid',
                                    borderColor: isMapped ? 'success.main' : 'divider',
                                    borderRadius: 1,
                                    '&:hover': {
                                      bgcolor: isMapped ? 'success.light' : 'action.hover'
                                    }
                                  }}
                                >
                                  <ListItemIcon>
                                    {isMapped && getMatchIcon(mappedColumn?.matchType)}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight="medium">
                                          {column.name}
                                        </Typography>
                                        <Chip 
                                          size="small" 
                                          label={column.type || 'TEXT'} 
                                          variant="outlined"
                                        />
                                        {isMapped && (
                                          <Chip
                                            size="small"
                                            color={getColumnMatchColor(mappedColumn?.matchType)}
                                            label={mappedColumn?.matchType || 'mapped'}
                                          />
                                        )}
                                      </Box>
                                    }
                                    secondary={isMapped ? `← Mapped from: ${mappedColumn?.name}` : null}
                                  />
                                </ListItem>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </CardContent>
              </Card>
            </Grid>

            {/* New File Columns */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon color="secondary" />
                    New File Columns
                    <Chip size="small" label={`${normalizedNewColumns.length} columns`} />
                  </Typography>
                  
                  <Droppable droppableId="newColumns" type="COLUMN">
                    {(provided, snapshot) => (
                      <List
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{ 
                          minHeight: 300,
                          maxHeight: 400,
                          overflow: 'auto',
                          bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1
                        }}
                      >
                        {normalizedNewColumns.map((column, index) => {
                          const isMapped = columnMappings.hasOwnProperty(index);
                          const mappedColumn = mappedNewColumns.find(c => c.originalIndex === index);
                          const isUnmapped = unmappedColumns.some(c => c.originalIndex === index);
                          
                          return (
                            <Draggable 
                              key={`new-${index}`} 
                              draggableId={`new-${index}`} 
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  sx={{
                                    mb: 1,
                                    bgcolor: isUnmapped ? 'warning.light' : 
                                             isMapped ? 'info.light' : 'background.paper',
                                    border: '1px solid',
                                    borderColor: isUnmapped ? 'warning.main' : 
                                                 isMapped ? 'info.main' : 'divider',
                                    borderRadius: 1,
                                    cursor: 'grab',
                                    '&:hover': {
                                      bgcolor: isUnmapped ? 'warning.light' : 
                                               isMapped ? 'info.light' : 'action.hover'
                                    },
                                    ...(snapshot.isDragging && {
                                      transform: `${provided.draggableProps.style?.transform} rotate(5deg)`,
                                      boxShadow: 3
                                    })
                                  }}
                                >
                                  <ListItemIcon>
                                    <DragIcon />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight="medium">
                                          {column.name}
                                        </Typography>
                                        <Chip 
                                          size="small" 
                                          label={column.type || 'TEXT'} 
                                          variant="outlined"
                                        />
                                        {isMapped && (
                                          <Chip
                                            size="small"
                                            color={getColumnMatchColor(mappedColumn?.matchType)}
                                            label={mappedColumn?.matchType || 'mapped'}
                                          />
                                        )}
                                        {isUnmapped && (
                                          <Chip
                                            size="small"
                                            color="warning"
                                            label="new"
                                          />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      isMapped ? `→ Maps to: ${mappedColumn?.mappedToColumn?.name}` :
                                      isUnmapped ? "Will be added as new column" : null
                                    }
                                  />
                                  {isMapped && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveMapping(index)}
                                      title="Remove mapping"
                                    >
                                      <ErrorIcon />
                                    </IconButton>
                                  )}
                                </ListItem>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DragDropContext>

        <Divider sx={{ my: 3 }} />

        {/* Merge Strategy Options */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Merge Strategy
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="success">
                <Typography variant="subtitle2">What will happen:</Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Add {unmappedColumns.length} new columns to the existing table</li>
                  <li>Map {mappedNewColumns.length} columns to existing columns</li>
                  <li>Import only unique records (avoid duplicates)</li>
                  <li>Preserve existing data integrity</li>
                </ul>
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmMerge}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <LinearProgress /> : <CheckIcon />}
        >
          {loading ? 'Processing...' : 'Confirm Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnMergeDialog;
